import React from "react";
import { cn } from "../../lib/utils";
import { Search, Download } from 'lucide-react';
import {
  whiteBG,
  semiBoldText,
  smallTextSize,
  largeTextSize,
  primaryText,
  textBlue,
  borderPrimary,
  placeholderPrimary,
  tertiaryText,
  darkGreenColor,
} from "../../styling/stylingHelper";

interface CardWithHeaderProps {
  title?: string;
  onboardingId?: string;
  titleOnboardingId?: string;
  seeAllText?: string;
  onSeeAllClick?: () => void;
  children: React.ReactNode;
  className?: string;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Optional stable anchors for contextual page tours. */
  seeAllOnboardingId?: string;
  searchOnboardingId?: string;
  onExport?: () => void;
  exportButtonText?: string;
  headerActions?: React.ReactNode;
  headerActionsRight?: React.ReactNode;
  /** Optional full-width row rendered between the header row and the content. */
  subHeader?: React.ReactNode;
}

export const CardWithHeader: React.FC<CardWithHeaderProps> = ({
  title,
  onboardingId,
  titleOnboardingId,
  seeAllText,
  onSeeAllClick,
  children,
  className,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  seeAllOnboardingId,
  searchOnboardingId,
  onExport,
  exportButtonText = "Export",
  headerActions,
  headerActionsRight,
  subHeader,
}) => {
  return (
    <div
      data-onboarding={onboardingId}
      className={cn(
        "flex flex-col items-start gap-4 p-4 relative",
        whiteBG,
        "rounded-xl",
        className
      )}
    >
      {title && (
        <div className="flex max-lg:flex-wrap items-center justify-between relative self-stretch w-full flex-[0_0_auto] gap-4">
          <div
            data-onboarding={titleOnboardingId}
            className={cn(
              "relative w-fit",
              largeTextSize,
              semiBoldText,
              primaryText
            )}
          >
            {title}
          </div>
          <div className="flex items-center gap-4">
            {headerActions && (
              <div className="flex items-center gap-3">
                {headerActions}
              </div>
            )}
            {searchQuery !== undefined && onSearchChange && (
              <div className="relative w-full max-w-[400px] min-w-[150px] shrink">
                <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none", tertiaryText)} />
                <input
                  type="text"
                  data-onboarding={searchOnboardingId}
                  placeholder={searchPlaceholder || "Search..."}
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className={cn(
                    "w-full pl-10 pr-4 py-2 rounded-xl border",
                    borderPrimary, whiteBG, primaryText, placeholderPrimary,
                    "focus:outline-none focus:ring-1 focus:ring-darkGreenColor dark:focus:ring-darkGreenColor-dark"
                  )}
                />
              </div>
            )}
            {headerActionsRight && (
              <div className="flex items-center gap-3 shrink-0 whitespace-nowrap">
                {headerActionsRight}
              </div>
            )}
            {onExport && (
              <button
                onClick={onExport}
                className={cn(
                  "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border transition-colors whitespace-nowrap shrink-0",
                  "border-darkGreenColor dark:border-darkGreenColor-dark bg-white dark:bg-background-dark hover:bg-neutral-50 dark:hover:bg-backgroundDarker-dark",
                  smallTextSize,
                  semiBoldText
                )}
                style={{ color: darkGreenColor }}
              >
                <Download className="w-4 h-4" />
                <span>{exportButtonText}</span>
              </button>
            )}
            {seeAllText && (
              <div
                className="inline-flex items-center justify-center gap-2 relative flex-[0_0_auto] cursor-pointer"
                data-onboarding={seeAllOnboardingId}
                onClick={onSeeAllClick}
              >
                <div
                  className={cn(
                    "relative w-fit",
                    smallTextSize,
                    semiBoldText,
                    textBlue
                  )}
                >
                  {seeAllText}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {subHeader && <div className="self-stretch w-full">{subHeader}</div>}
      {children}
    </div>
  );
};
